/**
 * i18n/index.ts — i18next bootstrap for the AstroGenesis web client.
 *
 * The message catalogue is the generated, flat-key ui.json under
 * ./messages/<locale>/ (the shared UI catalogue; 1,939 segments per locale).
 * Do NOT hand-edit those JSON files — regenerate them instead.
 *
 * Adding a locale:
 *   1. drop  src/i18n/messages/<code>/ui.json
 *   2. add ONE line to UI_MESSAGES below
 *   3. add ONE entry to LOCALES in ./locales.ts (dir, native name)
 *
 * Resources are inlined (no async backend), so init completes
 * synchronously and the first paint already renders the detected
 * language. Fallback is always "en".
 */
import ICU from "i18next-icu";
import i18next from "i18next";
import { initReactI18next } from "react-i18next";

import { detectLocale } from "./detect";
import { DEFAULT_LOCALE, SUPPORTED_LOCALE_CODES } from "./locales";
import { track } from "../lib/analytics";
import enUi from "./messages/en/ui.json";
import arUi from "./messages/ar/ui.json";
import arXbUi from "./messages/ar-XB/ui.json";
import bnUi from "./messages/bn/ui.json";
import deUi from "./messages/de/ui.json";
import frUi from "./messages/fr/ui.json";
import hiUi from "./messages/hi/ui.json";
import idUi from "./messages/id/ui.json";
import jaUi from "./messages/ja/ui.json";
import taUi from "./messages/ta/ui.json";
import teUi from "./messages/te/ui.json";
import urUi from "./messages/ur/ui.json";
import zhHansUi from "./messages/zh-Hans/ui.json";

// ── The one-line-per-locale message registry ──────────────────────────
const UI_MESSAGES: Record<string, Record<string, string>> = {
  en: enUi as Record<string, string>,
  ar: arUi as Record<string, string>,
  "ar-XB": arXbUi as Record<string, string>,
  bn: bnUi as Record<string, string>,
  de: deUi as Record<string, string>,
  fr: frUi as Record<string, string>,
  hi: hiUi as Record<string, string>,
  id: idUi as Record<string, string>,
  ja: jaUi as Record<string, string>,
  ta: taUi as Record<string, string>,
  te: teUi as Record<string, string>,
  ur: urUi as Record<string, string>,
  "zh-Hans": zhHansUi as Record<string, string>,
};

const resources = Object.fromEntries(
  // Registered under the default "translation" namespace: the catalogue
  // is FLAT with dotted keys ("ui.tab.today"), so we also disable
  // key/ns separators and let t() resolve the literal key.
  Object.entries(UI_MESSAGES).map(([code, messages]) => [code, { translation: messages }]),
);

let initialized = false;
let telemetryBound = false;

function bindFallbackTelemetry(): void {
  if (telemetryBound) return;
  telemetryBound = true;

  // i18next emits these events when a key cannot be resolved in the active
  // language/resource bundle. Keep the payload deliberately coarse: locale
  // and message key are safe diagnostics, while user/chart values never enter
  // the event. The local analytics queue already honours Do Not Track.
  i18next.on("missingKey", (languages, _namespace, key) => {
    const requested = Array.isArray(languages) ? languages[0] : String(languages);
    if (requested === DEFAULT_LOCALE) return;
    track("i18n_fallback", {
      requested_locale: requested,
      fallback_locale: DEFAULT_LOCALE,
      key: String(key).slice(0, 120),
    });
  });
  i18next.on("failedLoading", (language, namespace) => {
    if (language === DEFAULT_LOCALE) return;
    track("i18n_fallback", {
      requested_locale: String(language),
      fallback_locale: DEFAULT_LOCALE,
      key: `load:${String(namespace).slice(0, 80)}`,
    });
  });
}

/**
 * initI18n — idempotent. Called automatically on module import (below)
 * with the full detection chain; main.tsx relies on that import-time
 * init happening before ReactDOM renders.
 */
export function initI18n(initialLocale?: string): typeof i18next {
  if (initialized) return i18next;
  initialized = true;
  bindFallbackTelemetry();
  void i18next
    .use(ICU) // ICU MessageFormat for plural/gender/select once copy needs it
    .use(initReactI18next)
    .init({
      lng: initialLocale ?? detectLocale(),
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: SUPPORTED_LOCALE_CODES,
      // Deliberately NOT nonExplicitSupportedLngs: on i18next 26 that flag
      // makes resource lookup lower-case the code ("zh-hans") while the
      // store keys keep their canonical case ("zh-Hans") — every t() then
      // silently falls back to English (found via LQA on /zh-Hans,
      // 2026-08-29). Detection already canonicalises regional tags
      // (zh-CN → zh-Hans) before init, so the flag is unnecessary.
      resources,
      // The generated catalogue is FLAT ("ui.tab.today") — resolve keys
      // literally instead of splitting on "." / ":".
      keySeparator: false,
      nsSeparator: false,
      // React already escapes rendered strings; double-escaping would
      // print &amp; into the UI.
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    });
  return i18next;
}

// Import-time init: every importer of "@/i18n" (app shell, components,
// tests) gets a ready translator with zero provider boilerplate. With
// inline resources this completes synchronously.
initI18n();

// Convenience re-exports so consumers only ever need "@/i18n".
export * from "./locales";
export { i18next as i18n };
